import React, { useState, useEffect } from "react";
import "./CompanySnapshot.css";
import { useDispatch, useSelector } from "react-redux";
import { userActions } from "../../redux/actions/user.actions";

/* ─── Helpers ─────────────────────────────────────────── */
const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleDateString("en-AU", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const initials = (name = "") =>
  name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();

const parseOverdue = (detail) => {
  if (!detail) return null;
  const m = detail.match(/(\d+)\s*day/);
  return m ? parseInt(m[1]) : null;
};

/* ─── SVG Icons ───────────────────────────────────────── */
const icons = {
  location:   ["M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z", "M12 10m-3 0a3 3 0 1 0 6 0a3 3 0 1 0-6 0"],
  users:      ["M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2", "M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8z", "M23 21v-2a4 4 0 0 0-3-3.87", "M16 3.13a4 4 0 0 1 0 7.75"],
  briefcase:  ["M20 7H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z", "M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"],
  dollar:     ["M12 1v22", "M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"],
  file:       ["M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z", "M14 2v6h6", "M16 13H8", "M16 17H8", "M10 9H8"],
  building:   ["M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z", "M9 22V12h6v10"],
  info:       ["M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z", "M12 8v4", "M12 16h.01"],
};

const SvgIcon = ({ name, className = "" }) => {
  const paths = icons[name];
  if (!paths) return null;
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      {(Array.isArray(paths) ? paths : [paths]).map((d, i) => <path key={i} d={d} />)}
    </svg>
  );
};

/* ─── Tag helpers ──────────────────────────────────────── */
const formTypeColor = (type) => {
  switch (type) {
    case "480": return "teal";
    case "484": return "blue";
    case "362": return "purple";
    case "RA71": return "green";
    case "Dividend": return "amber";
    default: return "gray";
  }
};

const statusColor = (status) => {
  if (!status || status === "") return "gray";
  if (status === "VALIDATED") return "green";
  if (status === "Prepared") return "amber";
  return "gray";
};

const docuStatusColor = (s) => {
  if (!s) return null;
  if (s === "fulfilled") return "green";
  if (s === "pending") return "amber";
  return "gray";
};

/* ─── Sub-components ───────────────────────────────────── */

const Tag = ({ label, variant = "gray", style }) => (
  <span className={`snap-tag snap-tag--${variant}`} style={style}>{label}</span>
);

const CardHeader = ({ icon, title }) => (
  <div className="snap-card__header">
    <div className="snap-card__title">
      <SvgIcon name={icon} className="snap-card__title-icon" />
      {title}
    </div>
  </div>
);

/* ─── Addresses Card ───────────────────────────────────── */
const AddressesCard = ({ co }) => {
  const roAddr = [co.RO_CARE_OF, co.RO_BUILDING, co.RO_STREET, `${co.RO_SUBURB || ''} ${co.RO_STATE || ''} ${co.RO_PCODE || ''}`].filter(Boolean);
  const pbAddr = [co.PB_BUILDING, co.PB_STREET, `${co.PB_SUBURB || ''} ${co.PB_STATE || ''} ${co.PB_PCODE || ''}`].filter(Boolean);
  const postalAddr = [co.POSTAL1, co.POSTAL2, co.POSTAL3, co.POST_STATE, co.POST_PCODE].filter(Boolean);

  return (
    <div className="snap-card snap-fade-up snap-delay-2">
      <CardHeader icon="location" title="Addresses" />
      <div className="snap-address-row">
        <div className="snap-address-row__label">Registered Office</div>
        <div className="snap-address-row__value">
          {roAddr.length > 0 ? roAddr.map((line, i) => <span key={i}>{line}<br /></span>) : <span className="snap-address-row__value--na">N/A</span>}
        </div>
      </div>
      <div className="snap-address-row">
        <div className="snap-address-row__label">Principal Place of Business</div>
        <div className="snap-address-row__value">
          {pbAddr.length > 0 ? pbAddr.map((line, i) => <span key={i}>{line}<br /></span>) : <span className="snap-address-row__value--na">N/A</span>}
        </div>
      </div>
      <div className="snap-address-row">
        <div className="snap-address-row__label">Postal Address</div>
        <div className="snap-address-row__value">
          {postalAddr.length > 0
            ? postalAddr.join(', ')
            : <span className="snap-address-row__value--na">N/A</span>
          }
        </div>
      </div>
    </div>
  );
};

/* ─── Officers Card ────────────────────────────────────── */
const OfficersCard = ({ officers }) => {
  const active = officers.filter((o) => !o.CEASED);
  const former = officers.filter((o) => o.CEASED);

  const OfficerRow = ({ o }) => {
    const ceased = !!o.CEASED;
    return (
      <div className="snap-officer-item">
        <div className="snap-officer-item__left">
          <div className={`snap-officer-item__avatar${ceased ? " snap-officer-item__avatar--ceased" : ""}`}>
            {initials(o.OFFICER_NAME)}
          </div>
          <div className="snap-officer-item__info">
            <div className="snap-officer-item__name">{o.OFFICER_NAME}</div>
            <div className="snap-officer-item__sub">
              {o.POSITION_HELD}
              {o.DEF_SIGN && <> · <Tag label="Default Signatory" variant="teal" /></>}
            </div>
          </div>
        </div>
        <div className="snap-officer-item__right">
          {ceased
            ? <><span className="snap-officer-item__date">Ceased {fmt(o.CEASED)}</span><Tag label="Resigned" variant="red" /></>
            : <span className="snap-officer-item__date">Appt. {fmt(o.APPOINTED)}</span>
          }
        </div>
      </div>
    );
  };

  return (
    <div className="snap-card snap-fade-up snap-delay-3">
      <CardHeader icon="users" title="Officers" />
      {active.length > 0 && (
        <div className="snap-officer-section">
          <div className="snap-officer-section__label">Current Directors ({active.length})</div>
          {active.map((o, i) => <OfficerRow key={o.RN || i} o={o} />)}
        </div>
      )}
      {former.length > 0 && (
        <div className="snap-officer-section">
          <div className="snap-officer-section__label">Former Directors ({former.length})</div>
          {former.map((o, i) => <OfficerRow key={o.RN || i} o={o} />)}
        </div>
      )}
      {active.length === 0 && former.length === 0 && (
        <div className="snap-empty-state">No officers found</div>
      )}
    </div>
  );
};

/* ─── Shareholders Card ────────────────────────────────── */
const ShareholdersCard = ({ shareholders }) => (
  <div className="snap-card snap-grid__span-2 snap-fade-up snap-delay-3">
    <CardHeader icon="briefcase" title="Shareholders" />
    {shareholders && shareholders.length > 0 ? (
      <table className="snap-data-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Class</th>
            <th>Shares</th>
            <th>Amount Paid</th>
            <th>Unpaid</th>
            <th>Benef. Owner</th>
            <th>Fully Paid</th>
            <th>Since</th>
          </tr>
        </thead>
        <tbody>
          {shareholders.map((sh, i) => (
            <tr key={sh.RN || i}>
              <td>
                <span>{sh.NAME}</span>
                {sh.EMAIL_ADDRESS && (
                  <div style={{ fontSize: "11.5px", color: "var(--snap-text-3)", marginTop: 2 }}>
                    <a href={`mailto:${sh.EMAIL_ADDRESS}`}>{sh.EMAIL_ADDRESS}</a>
                  </div>
                )}
              </td>
              <td><Tag label={sh.CLASS_CODE} variant="teal" /></td>
              <td><strong>{Intl.NumberFormat("en-US").format(sh.SUM_TOT_SHARES || 0)}</strong></td>
              <td>${(sh.AMOUNT_PAID || 0).toFixed(2)}</td>
              <td>{(sh.AMOUNT_UNPAID || 0) > 0 ? <Tag label={`$${sh.AMOUNT_UNPAID}`} variant="red" /> : <Tag label="Nil" variant="gray" />}</td>
              <td><Tag label={sh.BEN_OWNER ? "Yes" : "No"} variant={sh.BEN_OWNER ? "green" : "gray"} /></td>
              <td><Tag label={sh.FULLY_PAID ? "Yes" : "No"} variant={sh.FULLY_PAID ? "green" : "amber"} /></td>
              <td style={{ whiteSpace: "nowrap", color: "var(--snap-text-2)", fontSize: "12px" }}>{fmt(sh.CHANGE_DATE)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    ) : (
      <div className="snap-empty-state">No shareholders found</div>
    )}
  </div>
);

/* ─── Share Capital Card ───────────────────────────────── */
const ShareCapitalCard = ({ sharecaps }) => (
  <div className="snap-card snap-grid__span-2 snap-fade-up snap-delay-4">
    <CardHeader icon="dollar" title="Share Capital" />
    {sharecaps && sharecaps.length > 0 ? (
      sharecaps.map((sc, i) => (
        <div key={sc.RN || i}>
          <div className="snap-share-stats">
            <div className="snap-share-stat">
              <div className="snap-share-stat__label">Class</div>
              <div className="snap-share-stat__value" style={{ fontSize: 16 }}>
                <Tag label={`${sc.CLASS_CODE} — ${sc.CLASS_DESC || 'Ordinary'}`} variant="teal" style={{ fontSize: 13, padding: "4px 10px" }} />
              </div>
              <div className="snap-share-stat__sub">Ordinary Shares</div>
            </div>
            <div className="snap-share-stat">
              <div className="snap-share-stat__label">Issued</div>
              <div className="snap-share-stat__value">{Intl.NumberFormat("en-US").format(sc.ISSUED || 0)}</div>
              <div className="snap-share-stat__sub">Total shares issued</div>
            </div>
            <div className="snap-share-stat">
              <div className="snap-share-stat__label">Paid Up</div>
              <div className="snap-share-stat__value snap-share-stat__value--green">${(sc.PAID_UP || 0).toFixed(2)}</div>
              <div className="snap-share-stat__sub">Fully paid @ ${(sc.NOM_VALUE || 0).toFixed(2)} / share</div>
            </div>
            <div className="snap-share-stat">
              <div className="snap-share-stat__label">Unpaid</div>
              <div className={`snap-share-stat__value ${(sc.DUE_AND_PAYABLE || 0) > 0 ? "" : "snap-share-stat__value--muted"}`}>
                ${(sc.DUE_AND_PAYABLE || 0).toFixed(2)}
              </div>
              <div className="snap-share-stat__sub">{(sc.DUE_AND_PAYABLE || 0) > 0 ? "Due & payable" : "No liability"}</div>
            </div>
          </div>
        </div>
      ))
    ) : (
      <div className="snap-empty-state">No share capital data found</div>
    )}
  </div>
);

/* ─── Document History Card ────────────────────────────── */
const DocumentHistoryCard = ({ documents }) => {
  const [filter, setFilter] = useState("All");
  const docs = documents || [];
  const formTypes = ["All", ...Array.from(new Set(docs.map((d) => d.FORM_TYPE)))];

  const filtered = filter === "All" ? docs : docs.filter((d) => d.FORM_TYPE === filter);

  return (
    <div className="snap-card snap-grid__span-2 snap-fade-up snap-delay-5">
      <CardHeader icon="file" title="Document History" />
      {docs.length > 0 ? (
        <>
          <div className="snap-doc-filter">
            <span className="snap-doc-filter__label">Filter by Type</span>
            <select className="snap-doc-filter__select" value={filter} onChange={(e) => setFilter(e.target.value)}>
              {formTypes.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--snap-text-3)" }}>
              {filtered.length} document{filtered.length !== 1 ? "s" : ""}
            </span>
          </div>
          <table className="snap-data-table">
            <thead>
              <tr>
                <th>Form</th>
                <th>Description</th>
                <th>Form Date</th>
                <th>Lodge Date</th>
                <th>ASIC Doc No.</th>
                <th>Status</th>
                <th>eSign</th>
                <th>Overdue</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((doc, i) => {
                const days = parseOverdue(doc.STATUS_DETAIL);
                const color = doc.ASIC_DOC_NO ? statusColor(doc.STATUS) : "gray";
                const dsColor = docuStatusColor(doc.DOCUSTATUS);

                return (
                  <tr key={doc.RN || i}>
                    <td><Tag label={doc.FORM_TYPE} variant={formTypeColor(doc.FORM_TYPE)} /></td>
                    <td style={{ fontSize: 12, color: "var(--snap-text-2)", maxWidth: 200 }}>{doc.FORM_TYPE_DESC || "—"}</td>
                    <td style={{ whiteSpace: "nowrap", fontSize: 12 }}>{fmt(doc.FORM_DATE)}</td>
                    <td style={{ whiteSpace: "nowrap", fontSize: 12 }}>{fmt(doc.LODGE_DATE)}</td>
                    <td>
                      {doc.ASIC_DOC_NO
                        ? <span>{doc.ASIC_DOC_NO}</span>
                        : <span style={{ color: "var(--snap-text-3)", fontSize: 12 }}>—</span>
                      }
                    </td>
                    <td>
                      {doc.STATUS
                        ? <Tag label={doc.STATUS} variant={color} />
                        : <Tag label="Pending" variant="gray" />
                      }
                    </td>
                    <td>
                      {doc.ESIGNTYPE && dsColor
                        ? <><Tag label={doc.ESIGNTYPE} variant="blue" />{" "}<Tag label={doc.DOCUSTATUS} variant={dsColor} /></>
                        : <span style={{ color: "var(--snap-text-3)", fontSize: 12 }}>—</span>
                      }
                    </td>
                    <td>
                      {days != null
                        ? <span className="snap-overdue-badge">{days}d overdue</span>
                        : <span style={{ color: "var(--snap-text-3)", fontSize: 12 }}>—</span>
                      }
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      ) : (
        <div className="snap-empty-state">No documents found</div>
      )}
    </div>
  );
};

/* ─── Company Info Card ────────────────────────────────── */
const CompanyInfoCard = ({ co }) => (
  <div className="snap-card snap-fade-up snap-delay-4">
    <CardHeader icon="building" title="Company Info" />
    <div className="snap-info-row">
      <div className="snap-info-row__label">ABN</div>
      <div className="snap-info-row__value">{co.ABN || '—'}</div>
    </div>
    <div className="snap-info-row">
      <div className="snap-info-row__label">Type</div>
      <div className="snap-info-row__value">{co.ASC_CLASS_DESC || '—'}</div>
    </div>
    <div className="snap-info-row">
      <div className="snap-info-row__label">Sub-class</div>
      <div className="snap-info-row__value">{co.ASC_S_CLAS_DESC || '—'}</div>
    </div>
    <div className="snap-info-row">
      <div className="snap-info-row__label">Review Date</div>
      <div className="snap-info-row__value" style={{ color: "var(--snap-amber)", fontWeight: 600 }}>{fmt(co.REVIEW_DATE)}</div>
    </div>
    <div className="snap-info-row">
      <div className="snap-info-row__label">Place of Inc.</div>
      <div className="snap-info-row__value">{co.PLACE_INC || '—'}</div>
    </div>
    <div className="snap-info-row">
      <div className="snap-info-row__label">Email</div>
      <div className="snap-info-row__value">
        {co.EMAIL_ADDRESS ? <a href={`mailto:${co.EMAIL_ADDRESS}`}>{co.EMAIL_ADDRESS}</a> : "—"}
      </div>
    </div>
    <div className="snap-info-row">
      <div className="snap-info-row__label">Agent</div>
      <div className="snap-info-row__value" style={{ fontSize: 12 }}>{co.CA_AGENT_NAME || "—"}</div>
    </div>
    <div className="snap-info-row">
      <div className="snap-info-row__label">Client Code</div>
      <div className="snap-info-row__value">{co.CLIENT_ACCOUNT_CODE || '—'}</div>
    </div>
  </div>
);

/* ─── Tab Configuration ───────────────────────────────── */
const TABS = ["Company Details", "Registers & Reports", "ASIC", "Forms & Documents", "eOrders", "Email History"];

/* ─── Root Component ───────────────────────────────────── */
const CompanySnapshotComponent = (props) => {
  const dispatch = useDispatch();
  const companyProfile = useSelector((state) => state.companyProfile);

  useEffect(() => {
    const selectedSearchValue = localStorage.getItem("inputSearchValue");
    const selectedCompanyCode = JSON.parse(localStorage.getItem("companyCode"));
    const selectedCompanyName = JSON.parse(localStorage.getItem("companyName"));

    if (props.searchCompany && props.searchCompany[0]) {
      dispatch(userActions.getCompanyProfileByCode(props.searchCompany[0].Code));
    } else if (selectedCompanyCode !== null) {
      props.setSearchCompany([
        { Code: selectedCompanyCode, Name: selectedCompanyName },
      ]);
      dispatch(userActions.getCompanyProfileByCode(selectedCompanyCode));
    } else if (selectedSearchValue !== null) {
      JSON.parse(selectedSearchValue).map((value) =>
        dispatch(userActions.getCompanyProfileByCode(value.Code))
      );
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (props.searchCompany && props.searchCompany[0]) {
      dispatch(userActions.getCompanyProfileByCode(props.searchCompany[0].Code));
    }
    // eslint-disable-next-line
  }, [props.searchCompany]);

  // Loading state
  if (companyProfile && companyProfile.loading) {
    return (
      <div className="snapshot-page">
        <div className="snap-loading">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (companyProfile && companyProfile.error && companyProfile.error.indexOf("400") !== -1) {
    return (
      <div className="snapshot-page">
        <div className="snap-error">
          The company is not attached to your Department List.
        </div>
      </div>
    );
  }

  // No data state
  if (!companyProfile || !companyProfile.items) {
    return (
      <div className="snapshot-page">
        <div className="snap-empty-state">
          No company data available
        </div>
      </div>
    );
  }

  const { company: co, shareholders, sharecaps, officers, documents, documentHistory } = companyProfile.items;

  return (
    <div className="snapshot-page">
      <div className="snap-grid">
        <AddressesCard co={co} />
        <OfficersCard officers={officers || []} />
        <CompanyInfoCard co={co} />
        <ShareholdersCard shareholders={shareholders} />
        <ShareCapitalCard sharecaps={sharecaps} />
        <DocumentHistoryCard documents={documentHistory || documents} />
      </div>
    </div>
  );
};

export default CompanySnapshotComponent;
